import { useState, useCallback, useMemo } from 'react';
import { useGetPlannersByBuildingMutation } from '@/store/api/spaceConfigService/spaceConfigServiceApi';
import type { PlannerEventRow, Planner } from '@/types/planner.types';

export interface UseHandoffPlannersResult {
  planners: Planner[];
  isLoading: boolean;
  error: string | null;
  fetchPlanners: (selectedIds: Set<string>, events: PlannerEventRow[]) => void;
  reset: () => void;
}

export function useHandoffPlanners(): UseHandoffPlannersResult {
  const [getPlannersByBuilding] = useGetPlannersByBuildingMutation();
  const [planners, setPlanners] = useState<Planner[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPlanners = useCallback(
    (selectedIds: Set<string>, events: PlannerEventRow[]) => {
      const selectedEvents = events.filter((e) => selectedIds.has(e.eventId));
      const uniqueBuildingIds = [
        ...new Set(selectedEvents.map((e) => e.buildingId).filter(Boolean)),
      ];

      if (uniqueBuildingIds.length === 0) {
        setPlanners([]);
        return;
      }

      setIsLoading(true);
      setError(null);

      void getPlannersByBuilding({ buildingIds: uniqueBuildingIds })
        .unwrap()
        .then((response) => {
          if (!response.success || !response.data) {
            setPlanners([]);
            setError('Failed to load planners');
            return;
          }

          const buildingPlannerSets = response.data.map(
            (bp) => new Map(bp.planners.map((p) => [p.email, p.displayName]))
          );

          if (buildingPlannerSets.length === 0) {
            setPlanners([]);
            return;
          }

          // Intersect all sets to find common planners
          const firstMap = buildingPlannerSets[0]!;
          const commonEmails = [...firstMap.keys()].filter((email) =>
            buildingPlannerSets.every((map) => map.has(email))
          );

          const result: Planner[] = commonEmails.map((email) => ({
            id: email,
            displayName: firstMap.get(email) ?? email,
            email,
          }));

          result.sort((left, right) =>
            left.displayName.localeCompare(right.displayName, undefined, { sensitivity: 'base' })
          );

          setPlanners(result);
        })
        .catch(() => {
          setError('Failed to load planners');
          setPlanners([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [getPlannersByBuilding]
  );

  const reset = useCallback(() => {
    setPlanners([]);
    setError(null);
    setIsLoading(false);
  }, []);

  return useMemo(
    () => ({ planners, isLoading, error, fetchPlanners, reset }),
    [planners, isLoading, error, fetchPlanners, reset]
  );
}
